import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { personService } from "services/PersonService";
import { getException } from "lib/helpers/errorHelper";
import { Person, SurveyPerson, File, FileDetail } from "types";

const name: string = "person.action";

interface Params {
  identificationNumber?: string | number;
  personId?: string;
  codeUp?: string;
  excelFileName?: string;
}
interface personState {
  persons: Person[];
  person: Person;
  personShowToast: boolean;
  surveyPerson: SurveyPerson | null;
  personError: boolean;
  personLoading: boolean;
  personSeverity: string;
  personResponse: any | null;
  fileLoading: boolean;
  fileError: boolean;
  files: File[];
  fileDetail: FileDetail[];
  businessSelected: string;
}

const initialState: personState = {
  persons: [],
  person: {
    completedSurvey: undefined,
    eMail: "",
    phone: "",
    fullName: "",
    jobTitle: "",
    identificationNumber: 0,
    nameUN: "",
    nameUP: "",
    noSurvey: 0,
    percentage: 0,
    personId: "",
    position: "",
    surveyLastUpdate: undefined,
    sentByEMail: undefined,
    sentBySMS: undefined,
    surveyStatus: "",
  },
  personError: false,
  personLoading: false,
  personResponse: null,
  personShowToast: false,
  personSeverity: "success",
  surveyPerson: null,
  fileLoading: false,
  fileError: false,
  files: [],
  fileDetail: [],
  businessSelected: "",
};

export const getPersonsByCompany = createAsyncThunk(
  `${name}/GetPersonsByCompany`,
  async (params: any) => await personService.getPersonsByCompany(params)
);

export const getPersonId = createAsyncThunk(`${name}/GetPersonId`, async (id: number, { rejectWithValue }) => {
  try {
    const response = await personService.getPersonId(id);
    return response;
  } catch ({ data }) {
    return rejectWithValue(getException(data));
  }
});

export const getPerson = createAsyncThunk(
  `${name}/getPerson`,
  async ({ identificationNumber, personId }: Params, { rejectWithValue }) => {
    try {
      const response = await personService.getPerson({ identificationNumber, personId });
      return response;
    } catch ({ data }) {
      return rejectWithValue(getException(data));
    }
  }
);

export const savePerson = createAsyncThunk(`${name}/savePerson`, async (person: SurveyPerson, { rejectWithValue }) => {
  try {
    const response = await personService.savePerson(person);
    return response;
  } catch ({ data }) {
    return rejectWithValue(getException(data));
  }
});

export const getSumaryExcelFilesLog = createAsyncThunk(
  `${name}/getSumaryExcelFilesLog`,
  async (codeUp: string, { rejectWithValue }) => {
    try {
      const response = await personService.getSumaryExcelFilesLog(codeUp);
      return response;
    } catch ({ data }) {
      return rejectWithValue(getException(data));
    }
  }
);

export const getDetailsExcelFilesLog = createAsyncThunk(
  `${name}/getDetailsExcelFilesLog`,
  async ({ codeUp = "", excelFileName = "" }: Params, { rejectWithValue }) => {
    try {
      const response = await personService.getDetailsExcelFilesLog(codeUp, excelFileName);
      return response;
    } catch ({ data }) {
      return rejectWithValue(getException(data));
    }
  }
);

export const personSlice = createSlice({
  name,
  initialState,
  extraReducers: (builder) => {
    /* GET PERSON BY COMPANY*/
    builder.addCase(getPersonsByCompany.pending, (state) => {
      state.personLoading = true;
    });

    builder.addCase(getPersonsByCompany.fulfilled, (state, { payload }) => {
      state.personLoading = false;
      state.persons = payload;
      state.personSeverity = "success";
    });

    builder.addCase(getPersonsByCompany.rejected, (state) => {
      state.personLoading = false;
      state.personSeverity = "error";
      state.personError = true;
    });
    /* GET PERSON BY PERSON ID*/
    builder.addCase(getPersonId.pending, (state) => {
      state.personLoading = true;
    });

    builder.addCase(getPersonId.fulfilled, (state, { payload }) => {
      state.personLoading = false;
      state.person = payload;
      state.personSeverity = "success";
    });

    builder.addCase(getPersonId.rejected, (state, { payload }) => {
      state.personError = true;
      state.personLoading = false;
      state.personSeverity = "error";
      state.personResponse = payload;
    });
    /* GET PERSON BY PERSON ID OR IDENTIFICATION*/
    builder.addCase(getPerson.pending, (state) => {
      state.personLoading = true;
    });

    builder.addCase(getPerson.fulfilled, (state, { payload }) => {
      state.personLoading = false;
      state.surveyPerson = payload;
      state.personSeverity = "success";
    });

    builder.addCase(getPerson.rejected, (state, { payload }) => {
      state.personError = true;
      state.personLoading = false;
      state.personShowToast = true;
      state.personSeverity = "error";
      state.personResponse = payload;
    });
    /** SAVE PERSON */
    builder.addCase(savePerson.pending, (state) => {
      state.personLoading = true;
    });
    builder.addCase(savePerson.fulfilled, (state, { payload }) => {
      state.personLoading = false;
      state.personResponse = payload;
      state.personShowToast = true;
      state.personSeverity = "success";
    });
    builder.addCase(savePerson.rejected, (state, { payload }) => {
      state.personError = true;
      state.personLoading = false;
      state.personShowToast = true;
      state.personSeverity = "error";
      state.personResponse = payload;
    });
    /** FILES UPLOADED */
    builder.addCase(getSumaryExcelFilesLog.pending, (state) => {
      state.fileLoading = true;
    });
    builder.addCase(getSumaryExcelFilesLog.fulfilled, (state, { payload }) => {
      state.fileLoading = false;
      state.files = payload;
    });
    builder.addCase(getSumaryExcelFilesLog.rejected, (state, { payload }) => {
      state.fileError = true;
      state.fileLoading = false;
    });
    /** FILE DETAILS */
    builder.addCase(getDetailsExcelFilesLog.pending, (state) => {
      state.fileLoading = true;
    });
    builder.addCase(getDetailsExcelFilesLog.fulfilled, (state, { payload }) => {
      state.fileLoading = false;
      state.fileDetail = payload;
    });
    builder.addCase(getDetailsExcelFilesLog.rejected, (state, { payload }) => {
      state.fileError = true;
      state.fileLoading = false;
    });
  },
  reducers: {
    hidePersonToast: (state) => {
      state.personShowToast = false;
    },
    clearSurveyPerson: (state) => {
      state.surveyPerson = null;
    },
    clearPersonResponse: (state) => {
      state.personResponse = null;
    },
    clearPersonError: (state) => {
      state.personError = false;
    },
    selectBusiness: (state, { payload }) => {
      state.businessSelected = payload;
    },
  },
});

export const { hidePersonToast, clearPersonResponse, clearSurveyPerson, clearPersonError, selectBusiness } =
  personSlice.actions;

export default personSlice.reducer;
